import React from 'react';
import axios from 'axios';
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Label,
  Input,
  FormGroup,
  Alert,
} from 'reactstrap';
import Swal from 'sweetalert2';
import debounce from 'lodash.debounce';
import UserEmployerAndRoleSelect from '../../../components/Users/UserEmployerAndRoleSelect';
import UserTagsSelect from '../../../components/Users/UserTagsSelect';
import UserFeatureSelected from '../../../components/Users/UserFeatureSelected';
import InboxQueryBranchesSelected from '../../../components/Users/InboxQueryBranchesSelected';
import UserSiteAssignmentSelect from '../../../components/Users/UserSiteAssignmentSelect';
import UserAreaAssignmentSelect from '../../../components/Users/UserAreaAssignmentSelect';
import TARoleSelect from '../../../components/Users/TARoleSelect';
import ManageMultiEmployerModal from '../Employer/ManageMultiEmployerModal';
import { EditTwoFactorModal } from './Modals';
import apiResponseHandler from '../../../utils/apiResponseHandler';
import { environment } from '../../../environment/environment';
import { userTAAssignmentApiService } from '../../../services/UserTAAssignmentApiService';
import { taRolesApiService } from '../../../services/TARolesApiService';
import { scopeApplicabilityForRoleCodename } from '../../../utils/taRoleScope';
import CustomIntlTelInput, { isMobileNumberValid } from '../../../components/Users/CustomIntlTelInput';

let cancelToken;

// /shift-sites/ and /shift-site-groups/ are paginated by the backend's
// BasicPagination, which differs from stock DRF in two ways that matter here:
// it caps page_size at 40 (max_page_size), and its get_paginated_response
// returns {count, pages, results} with no `next` link. So asking for a huge
// page_size silently yields 40 rows, and there is no `next` to follow. These
// feed multi-select dropdowns that need the complete list, so request the
// largest allowed page and then fetch the remaining pages by number.
const SCOPE_LIST_PAGE_SIZE = 40;

export const sanitizeMobileNumber = (mobileNumber) => {
  let sanitizedInput = mobileNumber ? mobileNumber.replaceAll(/[^\d]/g, '') : ''; // Remove special characters and spaces
  sanitizedInput = sanitizedInput.replace(/^0+/, ''); // Remove leading zeros
  return sanitizedInput;
};

export const mobileNumberLength = (mobileNumber) => {
  if (mobileNumber.substring(0, 1) === '0') {
    return 10;
  }
  if (
    mobileNumber.substring(0, 2) === '27' ||
    mobileNumber.substring(0, 1) === ''
  ) {
    return 11;
  }
  return 9;
};

class UserDetail extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      advancerequests: [],
      loading: false,
      save_data: {},
      employer: {},
      employer_features: [],
      user_features: [],
      user_tags: [],
      employers: [],
      availableRoles: [],
      smsLogs: [],
      groups: [],
      formError: false,
      errors: {
        mobileNumberErrors: [],
      },
      mobileNumberExists: 1, // Phone Number Component will validate this apon initial load
      mobile_number: {
        value: '',
        country_code: '27',
        valid: false,
      },
      isLoading: false,
      employer_branches: [],
      selected_branches: [],
      initialBranchIds: new Set(),
      isLoadingBranchMappings: false,
      isTwoFactorModalOpen: false,
      assignedSiteIds: [],
      assignedAreaIds: [],
      availableSites: [],
      availableAreas: [],
      assignmentsLoaded: false,
      taRoles: [],
      taRoleId: null,
      receiveNoClockinDigest: false,
      // Snapshot of the T&A assignment as loaded, to detect real changes on
      // save (so we don't re-write + re-toast it on every user save).
      initialTAAssignment: null,
    };

    this.handleEmailNotificationChange = this.handleEmailNotificationChange.bind(this);
    this.handleIntegrationsHealthOptOutChange = this.handleIntegrationsHealthOptOutChange.bind(this);
    this.openTwoFactorModal = this.openTwoFactorModal.bind(this);
    this.closeTwoFactorModal = this.closeTwoFactorModal.bind(this);
  }

  componentDidMount() {
    this.getUserData();
    this.getEmployers();
    this.getAvailableRoles();
    this.getUserBranchMappings();
    this.loadUserTAAssignment();
    this.loadTARoles();
  }

  loadUserTAAssignment = () => {
    const { userId } = this.props.match.params;
    userTAAssignmentApiService
      .getAssignment(userId)
      .then((data) => {
        this.setState({
          taRoleId: data.ta_role_id || null,
          assignedSiteIds: data.site_ids || [],
          assignedAreaIds: data.site_group_ids || [],
          receiveNoClockinDigest: data.receive_no_clockin_digest || false,
          initialTAAssignment: {
            taRoleId: data.ta_role_id || null,
            assignedSiteIds: data.site_ids || [],
            assignedAreaIds: data.site_group_ids || [],
            receiveNoClockinDigest: data.receive_no_clockin_digest || false,
          },
          assignmentsLoaded: true,
        });
      })
      .catch(() => {
        // Fetch failed — saveData will skip the PUT so a failed fetch
        // can't overwrite real state.
        this.setState({ assignmentsLoaded: false });
      });
  };

  // True if the T&A role / scope / digest opt-in differs from what was loaded,
  // so we only persist (and toast) when the section actually changed.
  taAssignmentChanged = () => {
    const initial = this.state.initialTAAssignment;
    if (!initial) return true; // no snapshot — be safe and persist
    const sameIds = (a, b) => {
      const x = [...(a || [])].sort();
      const y = [...(b || [])].sort();
      return x.length === y.length && x.every((v, i) => v === y[i]);
    };
    return (
      (this.state.taRoleId || null) !== (initial.taRoleId || null) ||
      (this.state.receiveNoClockinDigest || false) !==
        (initial.receiveNoClockinDigest || false) ||
      !sameIds(this.state.assignedSiteIds, initial.assignedSiteIds) ||
      !sameIds(this.state.assignedAreaIds, initial.assignedAreaIds)
    );
  };

  loadTARoles = () => {
    taRolesApiService
      .listRoles()
      .then((roles) => this.setState({ taRoles: roles }))
      .catch(() => this.setState({ taRoles: [] }));
  };

  loadAvailableSitesAndAreas = (employerId) => {
    if (!employerId) {
      this.setState({ availableSites: [], availableAreas: [] });
      return;
    }

    const resultsOf = (data) => (Array.isArray(data) ? data : data.results || []);

    // Total page count, preferring the API's own `pages`. Falls back to
    // deriving it from `count` so a response without `pages` still paginates.
    const pageCountOf = (data, pageSize) => {
      const pages = Number(data.pages);
      if (pages > 0) {
        return pages;
      }
      const count = Number(data.count);
      return count > 0 ? Math.ceil(count / pageSize) : 1;
    };

    const loadAllPages = (url, params) => {
      const fetchPage = (page) =>
        axios
          .get(url, { params: page > 1 ? { ...params, page } : params })
          .then((res) => res.data);

      return fetchPage(1).then((firstPage) => {
        if (Array.isArray(firstPage)) {
          return firstPage;
        }

        const firstItems = resultsOf(firstPage);
        const totalPages = pageCountOf(firstPage, params.page_size);
        if (totalPages <= 1) {
          return firstItems;
        }

        // Pages 2..n — the first page is already in hand.
        const remaining = Array.from({ length: totalPages - 1 }, (_, index) =>
          fetchPage(index + 2),
        );

        return Promise.all(remaining).then((laterPages) => [
          ...firstItems,
          ...laterPages.flatMap((page) => resultsOf(page)),
        ]);
      });
    };

    loadAllPages(`${environment.baseUrl}/shift-sites/`, {
      employer_id: employerId,
      page_size: SCOPE_LIST_PAGE_SIZE,
    })
      .then((sites) => {
        this.setState({ availableSites: sites });
      })
      .catch(() => {
        this.setState({ availableSites: [] });
      });
    loadAllPages(`${environment.baseUrl}/shift-site-groups/`, {
      employer_id: employerId,
      page_size: SCOPE_LIST_PAGE_SIZE,
    })
      .then((areas) => {
        this.setState({ availableAreas: areas });
      })
      .catch(() => {
        this.setState({ availableAreas: [] });
      });
  };

  getScopeApplicability = () => {
    const { taRoleId, taRoles = [] } = this.state;
    const role = taRoles.find((r) => r.id === taRoleId);
    return scopeApplicabilityForRoleCodename(role ? role.codename : null);
  };

  onTaRoleChange = (taRoleId) => {
    const { taRoles = [] } = this.state;
    const role = taRoles.find((r) => r.id === taRoleId);
    const { sitesApplicable, areasApplicable } =
      scopeApplicabilityForRoleCodename(role ? role.codename : null);
    this.setState((prev) => ({
      taRoleId,
      assignedSiteIds: sitesApplicable ? prev.assignedSiteIds : [],
      assignedAreaIds: areasApplicable ? prev.assignedAreaIds : [],
    }));
  };

  getUsers = debounce((params) => {
    this.setState({ isLoading: true });
    // Check if there are any previous pending requests
    if (typeof cancelToken !== typeof undefined) {
      cancelToken.cancel('Operation canceled due to new request.');
    }
    // Save the cancel token for the current request
    cancelToken = axios.CancelToken.source();

    axios
      .get(`${environment.baseUrl}/users/`, {
        params: { ...params },
        cancelToken: cancelToken.token,
      })

      .then((res) => {
        // check that the users are not the same
        this.setState({
          mobileNumberExists: res.data.filter(
            (user) =>
              user.mobile_number === params.search && user.id !== this.state.id
          ).length,

          isLoading: false,
        });
      })
      .catch(() => {
        this.setState({ isLoading: false });
      });
  }, 1000);

  getEmployers = () => {
    axios.get(`${environment.baseUrl}/employers/`).then((res) => {
      this.setState({ employers: res.data });
    });
  };

  getAvailableRoles = () => {
    let rolesFor = 'admin';
    if (this.state.employer) {
      rolesFor = 'all';
    }
    axios
      .get(`${environment.baseUrl}/user_roles?roles_for=${rolesFor}`)
      .then((res) => {
        this.setState({ availableRoles: res.data });
      });
  };

  getUserData = () => {
    this.setState({ loading: true });
    axios
      .get(`${environment.baseUrl}/users/${this.props.match.params.userId}/`)
      .then((res) => {
        const { selected_branches: _, ...userData } = res.data;
        this.setState(
          {
            ...userData,
            loading: false,
            mobile_number: {
              value: sanitizeMobileNumber(res.data.mobile_number),
              country_code: '27',
              valid: true,
            },
          },
          () => {
            if (res.data.employer && res.data.employer.id) {
              this.getEmployerData(res.data.employer.id);
            }
          }
        );
      });
  };

  getEmployers = () => {
    axios.get(`${environment.baseUrl}/employers/`).then((res) => {
      this.setState({ employers: res.data });
    });
  };

  addUserBranchMapping = (user_id, branch_id_list) => {
    this.setState({ loading: true });
    axios
      .post(`${environment.baseUrl}/add_user_branch_mapping/`, {
        user_id,
        branch_id_list,
      })
      .then((res) => {
        this.setState({ loading: false });
        Swal.fire({
          title: res.data.message,
          icon: 'success',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 10000,
          timerProgressBar: true,
        });
      })
      .catch((err) => {
        this.setState({ loading: false });
        Swal.fire({
          title: "An error occurred when trying to allocate a branch to a User",
          icon: 'error',
          toast: true,
          position: 'top',
          showConfirmButton: false,
          timer: 10000,
          timerProgressBar: true,
        });
      });
  };


  getUserBranchMappings = () => {
    this.setState({ isLoadingBranchMappings: true });
    axios
      .get(`${environment.baseUrl}/get_user_branch_mappings/${this.props.match.params.userId}/`)
      .then((res) => {
        const transformedBranches = res.data.branches.map(branch => ({
          id: branch.branch_id,
          name: branch.branch_name
        }));
        this.setState({ selected_branches: transformedBranches, initialBranchIds: new Set(transformedBranches.map(b => b.id)), isLoadingBranchMappings: false });
      })
      .catch(() => {
        this.setState({ isLoadingBranchMappings: false });
      });
  };

  getEmployerData = (employerId) => {
    axios
      .get(`${environment.baseUrl}/employers/${employerId}/`)
      .then((res) => {
        if (res.data.branches && res.data.branches.length > 0) {
          this.setState({
            employer_branches: res.data.branches
          });
        } else {
          this.setState({ loading: false });
        }
      })
      .catch((err) => {
        this.setState({ loading: false });
        console.error(err);
      });
    this.loadAvailableSitesAndAreas(employerId);
  };

  afterSetStateFinished(eventName) {
    if (eventName === 'employer') {
      this.getAvailableRoles();
      if (this.state.employer) {
        this.getEmployerData(this.state.employer);
      } else {
        this.setState({
          employer_branches: [],
          availableSites: [],
          availableAreas: [],
          taRoleId: null,
          assignedSiteIds: [],
          assignedAreaIds: [],
        });
      }
      this.getUserBranchMappings();
    }
  }

  onEmployerChanged = (event) => {
    this.handleinputchange(event);
  };

  onRolesChanged = (roleIds) => {
    this.setState({ groups: roleIds });
  };

  updatedUserFeatures = (features) => {
    this.setState({
      user_features: features,
    });
  };

  updatedBranchReceiveQueries = (selected_branches) => {
    this.setState({ selected_branches });
  };

  onTagsChanged = (tags) => {
    this.setState({
      user_tags: tags,
    });
  };

  handleinputchange = (event) => {
    event.preventDefault();
    const eventName = event.target.name;
    let eventValue = event.target.value;
    // this.form.validateFields(event.target);
    if (eventName === 'employer' && eventValue === 'NO_EMPLOYER') {
      eventValue = undefined;
    }
    if (
      event.target.name === 'bank_account_number' &&
      !/^\d+$/.test(event.target.value)
    ) {
      return null;
    }

    this.setState(
      {
        [eventName]: eventValue,
      },
      () => {
        this.afterSetStateFinished(eventName);
      }
    );
  };

  handleEmailNotificationChange(event) {
    this.setState({ receive_email_notifications: event.target.checked });
  }

  handleIntegrationsHealthOptOutChange(event) {
    this.setState({ integrations_health_email_opt_out: event.target.checked });
  }

  openTwoFactorModal() {
    this.setState({ isTwoFactorModalOpen: true });
  }

  closeTwoFactorModal() {
    this.setState({ isTwoFactorModalOpen: false });
  }

  validateInputs = () => {
    const errors = {
      mobileNumberErrors: [],
    };
    if (this.state.mobile_number.value && !this.state.mobile_number.valid) {
      errors.mobileNumberErrors.push('User mobile number is incorrect.');
    }

    this.setState({
      errors,
      formError: Object.values(errors).flat().length > 0,
    });
    return Object.values(errors).flat().length === 0;
  };

  saveData = () => {
    if (this.validateInputs()) {
      let employerId;
      if (typeof this.state.employer === 'object') {
        employerId = this.state.employer.id;
      } else {
        employerId = this.state.employer;
      }

      const payload = {
        ...this.state,
        mobile_number: sanitizeMobileNumber(this.state.mobile_number.value),
        employer: employerId,
        features: this.state.user_features.map((uF) => uF.feature.id),
        tags: this.state.user_tags.map((ut) => ut.tag.id),
      };

      axios
        .patch(
          `${environment.baseUrl}/users/${this.props.match.params.userId}/`,
          payload
        )
        .then((res) => {
          apiResponseHandler.handleApiSuccess('User Saved');
          this.getUserData();

          const { initialBranchIds, selected_branches } = this.state;
          const currentIds = selected_branches.map(b => b.id);
          const hasChanges = currentIds.length !== initialBranchIds.size || currentIds.some(id => !initialBranchIds.has(id));
          if (hasChanges) {
            this.addUserBranchMapping(this.props.match.params.userId, currentIds);
          }

          if (!this.state.assignmentsLoaded) {
            // Couldn't load existing assignments — don't overwrite them.
            apiResponseHandler.handleApiError(
              'Skipped saving T&A role/scope',
              new Error(
                'Current T&A assignment failed to load. Reload the page before saving.',
              ),
            );
          } else if (this.taAssignmentChanged()) {
            // Only persist (and toast) when the T&A section actually changed,
            // so a plain user save shows just "User Saved".
            const { sitesApplicable, areasApplicable } = this.getScopeApplicability();
            const assignmentPayload = {
              ta_role_id: this.state.taRoleId || null,
              site_ids: sitesApplicable ? this.state.assignedSiteIds || [] : [],
              site_group_ids: areasApplicable
                ? this.state.assignedAreaIds || []
                : [],
              receive_no_clockin_digest: this.state.taRoleId
                ? this.state.receiveNoClockinDigest
                : false,
            };
            userTAAssignmentApiService
              .setAssignment(this.props.match.params.userId, assignmentPayload)
              .then(() => {
                apiResponseHandler.handleApiSuccess('T&A assignment saved');
                // Refresh the snapshot so a follow-up save is a no-op.
                this.setState((prev) => ({
                  initialTAAssignment: {
                    taRoleId: prev.taRoleId || null,
                    assignedSiteIds: prev.assignedSiteIds || [],
                    assignedAreaIds: prev.assignedAreaIds || [],
                    receiveNoClockinDigest: prev.receiveNoClockinDigest || false,
                  },
                }));
              })
              .catch((error) =>
                apiResponseHandler.handleApiError(
                  'Failed to save T&A assignment',
                  error
                )
              );
          }
        })
        .catch((error) =>
          apiResponseHandler.handleApiError('Error while saving user', error)
        );
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  changeActive = (state) => {
    axios
      .patch(
        `${environment.baseUrl}/users/${this.props.match.params.userId}/`,
        { is_active: state }
      )
      .then((res) => {
        apiResponseHandler.handleApiSuccess('User Saved', res);
        this.getUserData();
      })
      .catch((error) =>
        apiResponseHandler.handleApiError('Error while saving user', error)
      );
  };

  deleteUser = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure you want to delete this user?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d63030',
      confirmButtonText: 'Yes, delete them!',
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(
            `${environment.baseUrl}/users/${this.props.match.params.userId}/`
          )
          .then((res) => {
            Swal.fire({
              title: `User deleted`,
              icon: 'success',
              toast: true,
              position: 'top',
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true,
            });
            this.props.history.push('/admin/users');
          })
          .catch((error) => {
            apiResponseHandler.handleApiError('Error', error);
          });
      }
    });
  };

  render() {
    const {
      loading,
      first_name,
      last_name,
      username,
      email,
      employers,
      mobile_number,
      is_active,
      errors,
      formError,
      receive_email_notifications,
      integrations_health_email_opt_out,
    } = this.state;
    return (
      <section className="dashboard">
        <div className="header bg-primary pb-6">
          <Container fluid>
            <div className="header-body">
              <Row className="align-items-center py-4">
                <Col lg="6">
                  <h6 className="h2 text-white d-inline-block mb-0">
                    {!!first_name && `${first_name} ${last_name} (${username})`}
                  </h6>
                </Col>
              </Row>
            </div>
          </Container>
        </div>
        <Container fluid className="mt--7">
          <Row className="pt-5 emp-edit-data">
            <Col>
              {loading ? (
                <Row className="my-3">
                  <Col className="d-flex justify-content-center">
                    <div className="spinner-border" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col xl={{ size: '6', offset: '3' }} lg={{ size: '10' }}>
                    <Row>
                      <Col>
                        <Card className="shadow">
                          <CardHeader>
                            <Row className="align-items-center">
                              <Col lg="8">
                                <h3 className="mb-0">Edit user</h3>
                              </Col>
                              <Col
                                style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                }}>
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={this.openTwoFactorModal}>
                                  OTP Login
                                </button>
                                <ManageMultiEmployerModal
                                  userFeatures={this.state.user_features}
                                  employerSelectedId={this.state.employer?.id}
                                  employers={this.state.employers}
                                  {...this.props}
                                />
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    this.changeActive(!is_active);
                                  }}>
                                  Make {is_active ? 'Inactive' : 'Active'}
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={this.deleteUser}>
                                  Delete
                                </button>
                              </Col>
                            </Row>
                          </CardHeader>
                          <CardBody>
                            {formError && (
                              <Alert color="danger">
                                {Object.values(errors)
                                  .flat()
                                  .map((error) => (
                                    <li key={error}>{error}</li>
                                  ))}
                              </Alert>
                            )}
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label for="first_name">First name</Label>
                                  <Input
                                    type="text"
                                    name="first_name"
                                    value={first_name}
                                    onChange={this.handleinputchange}
                                  />
                                </FormGroup>
                              </Col>
                              <Col>
                                <FormGroup>
                                  <Label for="last_name">Last name</Label>
                                  <Input
                                    type="text"
                                    name="last_name"
                                    value={last_name}
                                    onChange={this.handleinputchange}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label for="username">Username</Label>
                                  <Input
                                    type="text"
                                    name="username"
                                    value={username}
                                    onChange={this.handleinputchange}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label for="email">Email</Label>
                                  <Input
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={this.handleinputchange}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label for="mobile_number">
                                    Mobile number
                                  </Label>
                                  <CustomIntlTelInput
                                    customValidation={isMobileNumberValid}
                                    containerClassName="intl-tel-input"
                                    style={{ width: '100%' }}
                                    inputClassName="form-control"
                                    value={`${mobile_number?.value.length ? '+' : ''
                                      }${sanitizeMobileNumber(
                                        mobile_number.value
                                      )}`}
                                    formatOnInit
                                    onPhoneNumberChange={(
                                      valid,
                                      phone,
                                      selectedCountryData,
                                      fullNumber
                                    ) => {
                                      // check if user exists
                                      this.getUsers({
                                        search:
                                          sanitizeMobileNumber(fullNumber),
                                      });

                                      this.setState({
                                        mobile_number: {
                                          country_code:
                                            selectedCountryData.dialCode ||
                                            '27',
                                          value: fullNumber,
                                          valid,
                                        },
                                      });
                                    }}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <UserEmployerAndRoleSelect
                              onEmployerChanged={this.onEmployerChanged}
                              onRolesChanged={this.onRolesChanged}
                              employers={employers}
                              availableRoles={this.state.availableRoles}
                              employerSelectedId={this.state.employer?.id}
                              selectedRoleIds={this.state.groups}
                            />
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label for="features">Features</Label>
                                  <UserFeatureSelected
                                    userFeatures={this.state.user_features.filter(
                                      (e) => e.enabled
                                    )}
                                    employerFeatures={this.state.employer_features.filter(
                                      (e) => e.enabled
                                    )}
                                    updatedUserFeatures={
                                      this.updatedUserFeatures
                                    }
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label for="ta_role">Time &amp; Attendance Role</Label>
                                  <TARoleSelect
                                    availableRoles={this.state.taRoles}
                                    selectedRoleId={this.state.taRoleId}
                                    onChange={this.onTaRoleChange}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            {(() => {
                              const taScope = this.getScopeApplicability();
                              return (
                                <>
                                  {taScope.sitesApplicable && (
                                    <Row>
                                      <Col>
                                        <FormGroup>
                                          <Label for="assigned_sites">Assigned Sites</Label>
                                          <UserSiteAssignmentSelect
                                            availableSites={this.state.availableSites}
                                            selectedSiteIds={this.state.assignedSiteIds}
                                            onChange={(ids) =>
                                              this.setState({ assignedSiteIds: ids })
                                            }
                                          />
                                        </FormGroup>
                                      </Col>
                                    </Row>
                                  )}
                                  {taScope.areasApplicable && (
                                    <Row>
                                      <Col>
                                        <FormGroup>
                                          <Label for="assigned_areas">Assigned Areas</Label>
                                          <UserAreaAssignmentSelect
                                            availableAreas={this.state.availableAreas}
                                            selectedAreaIds={this.state.assignedAreaIds}
                                            onChange={(ids) =>
                                              this.setState({ assignedAreaIds: ids })
                                            }
                                          />
                                        </FormGroup>
                                      </Col>
                                    </Row>
                                  )}
                                  {this.state.taRoleId && (
                                    <Row>
                                      <Col>
                                        <FormGroup check className="mt-2">
                                          <Label check>
                                            <Input
                                              type="checkbox"
                                              checked={this.state.receiveNoClockinDigest === true}
                                              onChange={(e) =>
                                                this.setState({
                                                  receiveNoClockinDigest: e.target.checked,
                                                })
                                              }
                                            />
                                            {' '}Receive no-clock-in digest
                                          </Label>
                                          <p className="text-muted small mt-1">
                                            Send this user the per-site WhatsApp digest of employees who haven&apos;t clocked in by the cutoff, for the sites they can access. Off by default &mdash; only opted-in users receive it. Requires the employer&apos;s No-clock-in Manager Digest to be enabled.
                                          </p>
                                        </FormGroup>
                                      </Col>
                                    </Row>
                                  )}
                                </>
                              );
                            })()}
                            {this.state.employer_branches.length > 0 && (
                              <Row>
                                <Col>
                                  <FormGroup>
                                    <Label for="query_branches">Receive Queries from these branches</Label>
                                    <InboxQueryBranchesSelected
                                      queryBranches={this.state.employer_branches}
                                      selectedBranches={this.state.selected_branches}
                                      updatedBranchReceiveQueries={this.updatedBranchReceiveQueries}
                                      isDisabled={this.state.isLoadingBranchMappings}
                                    />
                                    {this.state.selected_branches.length === 0 && (
                                      <span style={{ color: '#FF922B' }}>
                                        At least one branch must be selected to receive queries.
                                      </span>
                                    )}
                                  </FormGroup>
                                </Col>
                              </Row>
                            )}
                            <Row>
                              <Col>
                                <FormGroup>
                                  <Label for="employerTag">Tags</Label>
                                  <UserTagsSelect
                                    employerTagsByCategory={
                                      this.state.employer_tags
                                    }
                                    userTags={this.state.user_tags}
                                    onTagsChanged={this.onTagsChanged}
                                  />
                                </FormGroup>
                              </Col>
                            </Row>
                            {this.state.employer_branches.length > 0 && (
                              <Row>
                                <Col>
                                  <FormGroup check>
                                    <Label check>
                                      <Input
                                        type="checkbox"
                                        checked={receive_email_notifications}
                                        onChange={this.handleEmailNotificationChange}
                                      />{' '}
                                      Receive emails for queries and leave approvals
                                    </Label>
                                  </FormGroup>
                                </Col>
                              </Row>
                            )}
                            <Row style={{ marginBottom: '10px' }}>
                              <Col>
                                <FormGroup check>
                                  <Label check>
                                    <Input
                                      type="checkbox"
                                      checked={!!integrations_health_email_opt_out}
                                      onChange={this.handleIntegrationsHealthOptOutChange}
                                    />{' '}
                                    Opt out of integrations health email notifications
                                  </Label>
                                </FormGroup>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <button
                                  className="btn btn-primary float-right"
                                  onClick={this.saveData}
                                  disabled={this.state.isLoading}>
                                  Save
                                </button>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Container>

        <EditTwoFactorModal
          isOpen={this.state.isTwoFactorModalOpen}
          onClose={this.closeTwoFactorModal}
          userId={this.state.id}
        />
      </section>
    );
  }
}

export default UserDetail;
